import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/services';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import type { NotificationInbox, NotificationItem } from '@/types';
import { NotificationContext, type NotificationContextValue } from './notification-context';

const POLL_MS = 60_000;
const TOAST_GAP_MS = 10_000;

/**
 * Loads the inbox on open, every minute while the tab is visible, and again
 * when the tab regains focus. The first load does not replay old toasts.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const notify = useUiStore((s) => s.notify);
  const [inbox, setInbox] = useState<NotificationInbox | null>(null);
  const [isOpen, setOpen] = useState(false);
  const watermark = useRef<string | null>(null);
  const lastToastAt = useRef(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const next = await getNotifications();
      setInbox(next);
      const newest = next.items.reduce(
        (max, item) => (item.alertAt > max ? item.alertAt : max),
        '',
      );
      if (watermark.current === null) {
        watermark.current = newest || new Date().toISOString();
        return;
      }
      const fresh = next.items.filter(
        (item) =>
          item.alertAt > (watermark.current ?? '') &&
          item.priority !== 'fyi' &&
          !item.expired &&
          !item.resolved,
      );
      watermark.current = newest || watermark.current;
      const lead = [...fresh].sort((a, b) => {
        if (a.priority === b.priority) {
          return b.alertAt.localeCompare(a.alertAt);
        }
        return a.priority === 'action' ? -1 : 1;
      })[0];
      if (lead && Date.now() - lastToastAt.current >= TOAST_GAP_MS) {
        lastToastAt.current = Date.now();
        notify(lead.body, 'info', {
          href: lead.href,
          durationMs: 5000,
          onClick: () => {
            void markNotificationRead(lead.id).then(setInbox).catch(() => undefined);
          },
        });
      }
    } catch {
      // A failed poll leaves the last inbox on screen.
    }
  }, [isAuthenticated, notify]);

  useEffect(() => {
    if (!isAuthenticated) {
      setInbox(null);
      setOpen(false);
      watermark.current = null;
      return;
    }

    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [isAuthenticated, refresh]);

  const markRead = useCallback(async (item: NotificationItem) => {
    try {
      setInbox(await markNotificationRead(item.id));
    } catch {
      setInbox((current) =>
        current
          ? {
              ...current,
              items: current.items.map((row) =>
                row.id === item.id ? { ...row, read: true } : row,
              ),
              unreadCount: Math.max(0, current.unreadCount - (item.read ? 0 : 1)),
            }
          : current,
      );
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setInbox(await markAllNotificationsRead());
  }, []);

  const value = useMemo<NotificationContextValue>(
    () => ({
      inbox,
      isOpen,
      openInbox: () => setOpen(true),
      closeInbox: () => setOpen(false),
      markRead,
      markAllRead,
    }),
    [inbox, isOpen, markRead, markAllRead],
  );

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
}
